/**
 * Shared DXF group-value codec helpers for the `objects/` OBJECTS-section
 * parsers and writers.
 *
 * These four functions are the single source of truth for converting
 * between DXF group string values and JS numbers in the OBJECTS-section
 * modules (annotative scale records, SPATIAL_FILTER, …). They were
 * formerly copy-pasted into each parser/writer sibling; extracted here so
 * one definition serves them all (CLAUDE.md priority #1/#2 — eliminate the
 * one-truth clone, keep a leaf util module with no upward deps to avoid a
 * depcruise cycle).
 *
 * `formatGroupNumber` matches the trailing-zero trimming convention used by
 * the top-level `writer.ts`, so values round-trip byte-faithfully against
 * AutoCAD-authored output regardless of which module emitted them.
 */

import type { DxfGroup } from "../dxfPrimitives";

/** Parse a DXF group value as a float, falling back to 0 for non-finite input. */
export function parseFloatSafe(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Parse a DXF group value as a base-10 int, falling back to 0 for non-finite input. */
export function parseIntSafe(value: string): number {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Format a number as a DXF group value, trimming trailing zeros (matches
 * the `writer.ts` convention so round-trip diffs stay minimal against
 * AutoCAD-authored output). Non-finite input formats as "0"; "-0" is
 * normalised to "0".
 */
export function formatGroupNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  if (Number.isInteger(value)) return String(value);
  const fixed = value.toFixed(12).replace(/\.?0+$/, "");
  return fixed === "-0" ? "0" : fixed;
}

/**
 * Append a DXF group to `out`, formatting numeric values via
 * `formatGroupNumber` and passing string values through unchanged.
 */
export function pushGroup(
  out: DxfGroup[],
  code: number,
  value: string | number,
): void {
  out.push({
    code,
    value: typeof value === "number" ? formatGroupNumber(value) : value,
  });
}
